import i18next from 'i18next';
import { AppLanguage, appLanguageOf } from 'config/languages';

/**
 * A country group of the league menu (England, Germany, International ...)
 * named in the player's language.
 *
 * The API names the groups in English and keys them by ISO country code
 * ("de", "gb-sct", "__intl__"), so a player reading Serbian saw "GERMANY,
 * SPAIN" next to "Fudbal". The browser's own region names (Intl.DisplayNames)
 * cover the countries; the few groups that are not countries, or not a whole
 * country (England, Scotland), are named here. The English name stays as the
 * fallback, and as the key everything else (routes, saved state) uses.
 */

type Labels = Record<AppLanguage, string>;

const OWN_NAMES: Record<string, Labels> = {
  __intl__: {
    en: 'International',
    de: 'International',
    sr: 'Međunarodno',
    hr: 'Međunarodno',
    bs: 'Međunarodno',
    it: 'Internazionale',
    fr: 'International',
    sq: 'Ndërkombëtare',
    tr: 'Uluslararası',
    es: 'Internacional',
  },
  __unk__: {
    en: 'Other',
    de: 'Sonstige',
    sr: 'Ostalo',
    hr: 'Ostalo',
    bs: 'Ostalo',
    it: 'Altro',
    fr: 'Autres',
    sq: 'Të tjera',
    tr: 'Diğer',
    es: 'Otros',
  },
  gb: {
    en: 'England',
    de: 'England',
    sr: 'Engleska',
    hr: 'Engleska',
    bs: 'Engleska',
    it: 'Inghilterra',
    fr: 'Angleterre',
    sq: 'Anglia',
    tr: 'İngiltere',
    es: 'Inglaterra',
  },
  'gb-sct': {
    en: 'Scotland',
    de: 'Schottland',
    sr: 'Škotska',
    hr: 'Škotska',
    bs: 'Škotska',
    it: 'Scozia',
    fr: 'Écosse',
    sq: 'Skocia',
    tr: 'İskoçya',
    es: 'Escocia',
  },
  'gb-nir': {
    en: 'Northern Ireland',
    de: 'Nordirland',
    sr: 'Severna Irska',
    hr: 'Sjeverna Irska',
    bs: 'Sjeverna Irska',
    it: 'Irlanda del Nord',
    fr: 'Irlande du Nord',
    sq: 'Irlanda e Veriut',
    tr: 'Kuzey İrlanda',
    es: 'Irlanda del Norte',
  },
  'gb-wls': {
    en: 'Wales',
    de: 'Wales',
    sr: 'Vels',
    hr: 'Wales',
    bs: 'Vels',
    it: 'Galles',
    fr: 'Pays de Galles',
    sq: 'Uells',
    tr: 'Galler',
    es: 'Gales',
  },
  // Intl's "Sjedinjene Američke Države" is too long for a menu row.
  us: {
    en: 'USA',
    de: 'USA',
    sr: 'SAD',
    hr: 'SAD',
    bs: 'SAD',
    it: 'USA',
    fr: 'États-Unis',
    sq: 'SHBA',
    tr: 'ABD',
    es: 'EE. UU.',
  },
};

// Serbian is written in Latin script here; Intl's plain "sr" is Cyrillic.
const INTL_LOCALE: Record<AppLanguage, string> = {
  en: 'en',
  de: 'de',
  sr: 'sr-Latn',
  hr: 'hr',
  bs: 'bs',
  it: 'it',
  fr: 'fr',
  sq: 'sq',
  tr: 'tr',
  es: 'es',
};

const regionNames = new Map<AppLanguage, Intl.DisplayNames | null>();
function regionsFor(lang: AppLanguage): Intl.DisplayNames | null {
  if (!regionNames.has(lang)) {
    try {
      regionNames.set(
        lang,
        new Intl.DisplayNames([INTL_LOCALE[lang]], { type: 'region' })
      );
    } catch {
      regionNames.set(lang, null);
    }
  }
  return regionNames.get(lang) ?? null;
}

export function leagueGroupName(
  name: string,
  code?: string | null,
  language?: string
): string {
  const lang = appLanguageOf(language ?? i18next.language);
  const key = (code ?? '').toLowerCase();
  const own = OWN_NAMES[key];
  if (own) return own[lang];
  if (/^[a-z]{2}$/.test(key)) {
    const region = regionsFor(lang)?.of(key.toUpperCase());
    // An unknown code comes back as itself ("ZZ").
    if (region && region.toUpperCase() !== key.toUpperCase()) return region;
  }
  return name;
}
